import { Component, Input, OnChanges } from '@angular/core';
import { Product } from '../../../model/product/product.model';

/**
 * One store line (a theme on a platform) as a row of the cart panel: the
 * theme's icon with the platform's logo as a badge on its corner (owner,
 * 2026-09-20: like the count on the cart's bag; no platform column, no "for
 * Sublime Text"), the CATEGORY / MODE eyebrow, its name ("Ocean Theme") and
 * its colour circles on the left; what the page projects into `[cardMeta]`
 * in the middle (the licence and the version) with "for <platform>" under
 * it; `[cardActions]` on the right
 * (price + quantity on the cart, the Download button on the order page).
 * Under the row, the theme's three tags as pills (owner, 2026-09-20: as on
 * the You might also like cards), then `[cardFooter]` (a warning, a note).
 * The platform is also in the link's label and the badge's title.
 */
@Component( {
  selector: 'app-store-item-card',
  templateUrl: './store-item-card.component.html',
  styleUrls: [ './store-item-card.component.scss' ],
} )
export class StoreItemCardComponent implements OnChanges {

  /** the theme; null when the site no longer knows the sku (renders from the names) */
  @Input() product: Product | null = null;
  @Input() category = '';
  /** fallback name when `product` is null */
  @Input() productName = '';
  @Input() platformName = '';
  @Input() platformLogo = '';
  /** a theme's color palette (2026-10-12), not a theme port: named "Ocean Palette", a brush on the icon's corner */
  @Input() palette = false;
  /** an app (2026-10-14: the macOS Color Picker), not a theme: keeps its own name and links to its own page */
  @Input() app = false;
  /** the app's page ('/plugins/macos/color-picker'), when `app` */
  @Input() appUrl = '';
  /** dims the row (an item that is no longer available) */
  @Input() muted = false;

  colors: string[] = [];
  tags: string[] = [];

  ngOnChanges(): void {
    this.colors = ( this.product?.compactPalette || this.product?.palette || [] ).slice( 0, 5 );
    this.tags = ( this.product?.tags || [] ).slice( 0, 3 );
  }

  /** "Ocean Theme": the theme's name with the word Theme, as the store names it; "Ocean Palette" for a palette. */
  get name(): string {
    if ( this.app ) return ( this.product?.name || this.productName || '' ).trim();
    const base = ( this.product?.name || this.productName || '' ).trim().replace( /\s+(theme|palette)$/i, '' );
    if ( !base ) return '';
    return `${ base } ${ this.palette ? 'Palette' : 'Theme' }`;
  }

  /** "for Sublime Text", under the version in the middle column */
  get platformLabel(): string {
    return this.platformName ? `for ${ this.platformName }` : '';
  }

  get themeQuery(): { [ key: string ]: string } | null {
    return this.product && !this.app ? { product: this.product.key, category: ( this.category || '' ).toLowerCase() } : null;
  }
}
